import {useQuery} from "@tanstack/react-query"
import moment from "moment"
import {getFullName, handleError, toDateOnly} from "helpers"
import {useCalendarEventsViewSettings} from "hooks/useCalendarEventViewSettings"
import {debounce, forEach, get, isEmpty, keyBy, partition, sortBy} from "lodash"
import {useEffect, useMemo, useRef, useState} from "react"
import {calendarService, coreSocket} from "services"
import {useModel} from "hooks"
import {SocketEvent} from "helpers/socket"
import {CalendarEventType, CalendarStaticFilter, LinkedCalendarInfo, StaticOption} from "types/calendar"
import {
    checkIsKeepSystemDateEvent,
    checkIsTermMajorSystemEvent,
    getEndDateTime,
    getReminderOptionsByIds,
    getStartDateTime
} from "helpers/calendar"
import {REMINDERS} from "sections/calendar/parts/common/constants"
import {useGetExtraEventInfo} from "./useGetExtraEventInfo"
import {useMyLinkedCalendars} from "./useMyLinkedCalendars"
import {useMyCalendarSettings} from "./useMyCalendarSettings"
import {ShiftWorkWeek, WorkWeekEventType} from "types/attendance"
import RRule from "rrule"
import {v4 as uuid} from "uuid"

const calendarReminderOptions = REMINDERS.map((reminder) => ({value: reminder.id, label: reminder.name}))

const DEFAULT_STATIC_OPTIONS: StaticOption[] = [
    {id: CalendarStaticFilter.Term, name: "Public Terms", isShow: true},
    {id: CalendarStaticFilter.ProgramStart, name: "Public Program Start", isShow: true},
    {id: CalendarStaticFilter.ProgramEnd, name: "Public Program End", isShow: true},
    {id: CalendarStaticFilter.WorkSchedules, name: "Public Work Schedules", isShow: true},
    {id: CalendarStaticFilter.Booked, name: "Booked Events", isShow: true},
    {id: CalendarStaticFilter.CourseSchedules, name: "Course Schedules", isShow: true},
    {id: CalendarStaticFilter.Invites, name: "Invites", isShow: true}
]

const RRULE_WEEKDAYS = [
    RRule.SU, // 0 = Sunday
    RRule.MO,
    RRule.TU,
    RRule.WE,
    RRule.TH,
    RRule.FR,
    RRule.SA
]

export const useGetCalendarEvents = (
    data?: Partial<{
        shiftData: {
            shiftWorkWeeks: ShiftWorkWeek[]
            startDate: string
            contractEndDate: string
            projectedEndDate: string
        }
        invitedUserId?: number
    }>
) => {
    const {shiftData, invitedUserId} = data || {}
    const model = useModel()
    const userId = +get(model, "user.id")
    const [dateRange, setDateRange] = useState(null)
    const [events, setEvents] = useState([])
    const [viewMode, setViewMode] = useState<"list" | "calendar">("calendar")
    const {settings, isLoadingSettings} = useCalendarEventsViewSettings()
    const [linkedCalendars, setLinkedCalendars] = useState<LinkedCalendarInfo[]>([])
    const [staticOptions, setStaticOptions] = useState<StaticOption[]>([])
    const {getEventColor, getModuleTitle, getSubModuleTitle, getUsers} = useGetExtraEventInfo()

    const isViewingOtherUser = !!model.viewOtherUserCalendar

    const {
        linkedCalendars: fetchedLinkedCalendars,
        primaryCalendarId,
        isLoading: isLoadingCalendar
    } = useMyLinkedCalendars({
        viewOtherUserId: model.viewOtherUserCalendar?.id,
        primaryUser: model.viewOtherUserCalendar || model.user
    })

    const {
        settings: calendarUserSettings,
        update: updateCalendarUserSettings,
        isLoading: isLoadingCalendarSettings
    } = useMyCalendarSettings({
        enabled: !isViewingOtherUser
    })

    const applySelectedCalendarIds = (
        calendars: LinkedCalendarInfo[],
        selectedCalendarIds: number[] | null | undefined
    ): LinkedCalendarInfo[] => {
        // First time / unset: show all calendars checked
        if (isEmpty(selectedCalendarIds)) {
            return calendars.map((calendar) => ({...calendar, isShow: true}))
        }

        const selectedSet = new Set(selectedCalendarIds.map(Number))
        return calendars.map((calendar) => ({...calendar, isShow: selectedSet.has(calendar.calendarId)}))
    }

    useEffect(() => {
        if (isEmpty(fetchedLinkedCalendars)) {
            setLinkedCalendars([])
            return
        }

        if (isViewingOtherUser) {
            setLinkedCalendars(fetchedLinkedCalendars)
            return
        }

        // Wait for settings before applying selection so the first paint matches saved prefs
        if (isLoadingCalendarSettings) {
            return
        }

        setLinkedCalendars(applySelectedCalendarIds(fetchedLinkedCalendars, calendarUserSettings?.selectedCalendarIds))
        // Only re-sync when the calendar list or initial settings load changes — not after our own saves
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedLinkedCalendars, isViewingOtherUser, isLoadingCalendarSettings])

    const saveSelectedCalendarIdsRef = useRef(updateCalendarUserSettings)
    saveSelectedCalendarIdsRef.current = updateCalendarUserSettings

    const debouncedSaveSelectedCalendarIds = useMemo(
        () =>
            debounce((selectedCalendarIds: number[]) => {
                void saveSelectedCalendarIdsRef.current({selectedCalendarIds})
            }, 400),
        []
    )

    useEffect(() => () => debouncedSaveSelectedCalendarIds.cancel(), [debouncedSaveSelectedCalendarIds])

    const updateLinkedCalendars = (data: LinkedCalendarInfo[]) => {
        setLinkedCalendars(data)
        if (isViewingOtherUser) {
            return
        }
        const selectedCalendarIds = data.filter((item) => item.isShow).map((item) => item.calendarId)
        debouncedSaveSelectedCalendarIds(selectedCalendarIds)
    }

    const {refetch: reloadEvents, isFetching} = useQuery(
        ["calendar-events", staticOptions, dateRange, linkedCalendars],
        () => getEvents(),
        {
            enabled: !isEmpty(staticOptions) && !isEmpty(dateRange) && !isEmpty(linkedCalendars),
            onSuccess: (response) => {
                const data = convertEvents(response)
                const dailySchedules = convertEvents(getDailySchedules())
                setEvents(sortEventsByStart([...data, ...dailySchedules]))
            },
            onError: (error) => handleError(error)
        }
    )

    // A calendar event changed somewhere in the platform. The push is only a signal —
    // refetch the visible window (source of truth) rather than trusting pushed data.
    // Gate on the affected range so navigating a different month doesn't refetch needlessly.
    const isRelevantCalendarChange = (payload?: {
        calendarIds?: number[]
        affectedRange?: {from: string; to: string} | null
    }) => {
        const range = payload?.affectedRange
        if (!range?.from || !range?.to || isEmpty(dateRange)) {
            // Unknown when/where it happened — refetch to stay correct.
            return true
        }
        const viewFrom = moment(dateRange.startStr).utc()
        const viewTo = moment(dateRange.endStr).utc()
        return moment(range.from).isSameOrBefore(viewTo) && moment(range.to).isSameOrAfter(viewFrom)
    }

    // Coalesce bursts of change events (e.g. bulk course-schedule/term generation, which
    // creates many events in a loop) into a single refetch. Without this, each event would
    // trigger its own window refetch + user-enrichment fetch. Trailing debounce with a
    // maxWait so a long-running generation still refreshes periodically, then once at the end.
    const reloadEventsRef = useRef(reloadEvents)
    reloadEventsRef.current = reloadEvents
    const debouncedReloadEvents = useMemo(() => debounce(() => reloadEventsRef.current(), 800, {maxWait: 3000}), [])
    useEffect(() => () => debouncedReloadEvents.cancel(), [debouncedReloadEvents])

    useEffect(() => {
        const handleCalendarEventUpdated = (payload?: {
            calendarIds?: number[]
            affectedRange?: {from: string; to: string} | null
        }) => {
            if (isRelevantCalendarChange(payload)) {
                debouncedReloadEvents()
            }
        }
        coreSocket.on(SocketEvent.CalendarEventUpdated, handleCalendarEventUpdated)
        return () => {
            coreSocket.off(SocketEvent.CalendarEventUpdated, handleCalendarEventUpdated)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, linkedCalendars])

    useEffect(() => {
        if (!isEmpty(settings)) {
            const newStaticOptions = DEFAULT_STATIC_OPTIONS.filter((item) => {
                switch (item.id) {
                    case CalendarStaticFilter.ProgramStart:
                        return settings.programStart
                    case CalendarStaticFilter.ProgramEnd:
                        return settings.programEnd
                    case CalendarStaticFilter.Term:
                        return settings.termStartAndEnd
                    default:
                        return true
                }
            })
            setStaticOptions(newStaticOptions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings])

    const sortEventsByStart = (events) => {
        const now = moment()
        const [pastEvents, upcomingEvents] = partition(events, (event) => moment(event.end).isBefore(now))

        return [
            ...sortBy(upcomingEvents, (event) => moment(event.start).valueOf()),
            ...sortBy(pastEvents, (event) => -moment(event.start).valueOf())
        ]
    }

    const getEvents = async () => {
        const {startStr: from, endStr: to} = dateRange
        const utcFrom = moment(from).utc().format()
        const utcTo = moment(to).utc().format()
        const checkedStaticOptionIds = staticOptions.filter((item) => item.isShow).map((item) => item.id)
        const checkedStaticFilter = Object.fromEntries(checkedStaticOptionIds.map((item) => [item, true]))
        const calendarIds = linkedCalendars.filter((item) => item.isShow).map((item) => item.calendarId)
        const filter = {
            from: utcFrom,
            to: utcTo,
            owner_user_ids: [userId],
            calendar_ids: calendarIds,
            invited_user_id: invitedUserId ? invitedUserId : undefined,
            ...checkedStaticFilter
        }
        const allEvents = await calendarService.getEvents(
            {filter, includeInvites: true},
            {viewOtherUserId: model.viewOtherUserCalendar?.id}
        )
        const userIds: number[] = []
        if (!isEmpty(allEvents)) {
            forEach(allEvents, (item) => {
                if (item.owner_user_id && item.owner_user_id !== userId) {
                    userIds.push(item.owner_user_id)
                }
                if (item.invited_user_ids?.length) {
                    forEach(item.invited_user_ids, (id) => userIds.push(id))
                }
            })
        }
        const users = await getUsers(userIds)
        const usersKeyById = keyBy(users, "id")
        forEach(allEvents, (event) => {
            const isCurrentUser = event.owner_user_id === userId
            event.ownerFullName = isCurrentUser
                ? getFullName(model.user)
                : getFullName(usersKeyById[event.owner_user_id])
            if (event.invited_user_ids?.length) {
                event.invitedUsers = event.invited_user_ids
                    .filter((id) => id !== event.owner_user_id)
                    .map((id) => usersKeyById[id])
                    .filter(Boolean)
            }
        })

        return allEvents
    }

    const getDailySchedules = () => {
        if (!shiftData) {
            return []
        }

        const allEvents = []
        const {shiftWorkWeeks, startDate, contractEndDate, projectedEndDate} = shiftData

        if (!startDate || (!projectedEndDate && !contractEndDate)) {
            return []
        }

        for (let shiftWorkWeek of shiftWorkWeeks) {
            const workWeek = shiftWorkWeek.workWeek ?? []
            const dailySchedules = workWeek
                .map((workDay) => {
                    if (workDay.allDay) {
                        workDay.startTime = "00:00"
                        workDay.endTime = "24:00"
                    }
                    return workDay
                })
                .filter((item) => item.startTime && item.endTime)

            for (let dailySchedule of dailySchedules) {
                const [startHour, startMinute] = dailySchedule.startTime.split(":").map(Number)
                const [endHour, endMinute] = dailySchedule.endTime.split(":").map(Number)
                const allDay = dailySchedule.allDay
                const rule = new RRule({
                    freq: RRule.WEEKLY,
                    dtstart: moment(toDateOnly(startDate)).toDate(),
                    until: moment(
                        projectedEndDate ? toDateOnly(projectedEndDate) : toDateOnly(contractEndDate)
                    ).toDate(),
                    byweekday: RRULE_WEEKDAYS[dailySchedule.dayOfWeek]
                })

                const dates = rule.all()
                dates.forEach((date) => {
                    const eventStart = moment(toDateOnly(date))
                    const eventEnd = moment(toDateOnly(date))
                    eventStart.set({
                        hour: startHour,
                        minute: startMinute,
                        second: 0,
                        ms: 0
                    })

                    eventEnd.set({
                        hour: endHour,
                        minute: endMinute,
                        second: 0,
                        ms: 0
                    })

                    allEvents.push({
                        name: shiftWorkWeek.name,
                        json_data: {
                            eventType: WorkWeekEventType.Daily,
                            sub_module_id: null,
                            allDay,
                            color: "#939393"
                        },
                        object_id: uuid(),
                        start_at_utc: moment(eventStart).utc(),
                        end_at_utc: moment(eventEnd).utc(),
                        dayOfWeek: dailySchedule.dayOfWeek
                    })
                })
            }
        }
        return allEvents
    }

    const convertEvents = (initialEvents, tz?: string) => {
        const timezone = tz ? tz : moment.tz.guess()
        return (initialEvents || []).map((event, index) => {
            const originalColor = event?.json_data?.color || "#1e90ff"
            const reminders = getReminderOptionsByIds(event?.json_data?.notifications ?? [], calendarReminderOptions)
            const withSMS = !!event?.json_data?.with_sms
            const withPushNotification = !!event?.json_data?.with_push_notification
            const withEmail = !!event?.json_data?.with_email
            const eventSignInRequired = !!event?.json_data?.eventSignInRequired
            const eventTrackAttendanceMinutes = !!event?.json_data?.eventTrackAttendanceMinutes
            const attendedChangeToStatus = !!event?.json_data?.attendedChangeToStatus
            const notAttendedChangeToStatus = !!event?.json_data?.notAttendedChangeToStatus
            const completionWorkflow = !!event?.json_data?.completionWorkflow
            const {borderColor, color} = getEventColor(event)
            const allDay =
                checkIsTermMajorSystemEvent(event.type) || event?.json_data?.all_day || event?.json_data?.allDay
            let startDateTime =
                checkIsKeepSystemDateEvent(event.type) && (event?.json_data?.all_day || event?.json_data?.allDay)
                    ? moment(event.start_at_wall).tz(timezone, true)
                    : getStartDateTime(event)
            let endDateTime =
                checkIsKeepSystemDateEvent(event.type) && (event?.json_data?.all_day || event?.json_data?.allDay)
                    ? startDateTime.clone().add(event.duration, "seconds").tz(timezone, true)
                    : getEndDateTime(event)

            if (tz) {
                startDateTime = moment.tz(event.start_at_utc, timezone)
                endDateTime = moment.tz(event.end_at_utc, timezone)
            }

            return {
                id: event.object_id,
                calendarId: event.calendar_id,
                start: startDateTime.format(),
                end: endDateTime.format(),
                startDateTime: startDateTime.format(),
                endDateTime: endDateTime.format(),
                originalColor,
                borderColor: borderColor,
                color: color,
                title: event.name,
                allDay,
                order: 1,
                classNames: "",
                initialEventIndex: index,
                description: event.description,
                isAppointmentSlot: event.type === CalendarEventType.AppointmentSlot,
                ownerUserId: event.owner_user_id,
                ownerFullName: event.ownerFullName,
                willGo: event.willGo,
                reminders,
                withSMS,
                withPushNotification,
                withEmail,
                eventSignInRequired,
                eventTrackAttendanceMinutes,
                attendedChangeToStatus,
                notAttendedChangeToStatus,
                completionWorkflow,
                isLesson: !isEmpty(event.courseScheduleInfo),
                type: event.type,
                isHoliday: event.type === CalendarEventType.WorkSchedule,
                schedule_suffix: event.courseScheduleInfo?.schedule_suffix,
                moduleTitle: getModuleTitle(event.json_data.eventType, event.courseInfo),
                subModuleTitle: getSubModuleTitle(event.json_data.sub_module_id, event.courseInfo),
                json_data: event.json_data,
                start_at_timezone: event.start_at_timezone,
                rrule: event.rrule,
                invitedUsers: event.invitedUsers
            }
        })
    }

    return {
        events,
        isLoading:
            isFetching || isLoadingCalendar || isLoadingSettings || (!isViewingOtherUser && isLoadingCalendarSettings),
        linkedCalendars,
        staticOptions,
        primaryCalendarId,
        updateLinkedCalendars,
        updateStaticOptions: setStaticOptions,
        setEvents,
        reloadEvents,
        setDateRange,
        dateRange,
        viewMode,
        setViewMode
    }
}
